import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { GraduationCap, ChefHat, ListChecks, TrendingUp, Calendar } from 'lucide-react';
import { format } from 'date-fns';

const DashboardPage = () => {
    const { getAuthHeaders, API } = useAuth();
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchStats();
    }, []);

    const fetchStats = async () => {
        try {
            const response = await axios.get(`${API}/dashboard/stats`, {
                headers: getAuthHeaders()
            });
            setStats(response.data);
        } catch (error) {
            console.error('Failed to fetch stats:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className="space-y-6">
                    <div className="skeleton h-8 w-48"></div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {[1, 2, 3, 4].map(i => (
                            <div key={i} className="skeleton h-32 rounded-xl"></div>
                        ))}
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <div className="skeleton h-48 rounded-xl"></div>
                        <div className="lg:col-span-2 skeleton h-48 rounded-xl"></div>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-8 page-enter" data-testid="dashboard-page">
                {/* Header */}
                <div>
                    <h1 className="font-heading text-3xl font-bold text-ica-navy">Dashboard</h1>
                    <p className="text-gray-500 mt-1">Overview of your chef school</p>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {[
                        { key: 'stat-students', label: 'Total Students', value: stats?.total_students || 0, icon: GraduationCap, to: '/students', accent: 'border-t-ica-navy', tint: 'bg-ica-navy/10', fg: 'text-ica-navy' },
                        { key: 'stat-lessons', label: 'Total Lessons', value: stats?.total_lessons || 0, icon: ChefHat, to: '/lessons', accent: 'border-t-ica-gold', tint: 'bg-ica-gold/10', fg: 'text-ica-gold' },
                        { key: 'stat-criteria', label: 'Grading Criteria', value: stats?.total_criteria || 0, icon: ListChecks, to: '/criteria', accent: 'border-t-emerald-500', tint: 'bg-emerald-50', fg: 'text-emerald-600' },
                        { key: 'stat-overview', label: 'Active Years', value: 3, icon: TrendingUp, to: '/assessments', accent: 'border-t-sky-500', tint: 'bg-sky-50', fg: 'text-sky-600' }
                    ].map(c => (
                        <div key={c.key} data-testid={c.key} onClick={() => navigate(c.to)}
                            className={`bg-white rounded-lg border border-gray-200 border-t-[3px] ${c.accent} p-5 cursor-pointer transition-all hover:border-ica-navy hover:shadow-md`}>
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <p className="text-sm font-medium text-gray-500">{c.label}</p>
                                    <p className="text-3xl font-heading font-bold text-ica-navy mt-2">{c.value}</p>
                                </div>
                                <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${c.tint}`}>
                                    <c.icon className={`w-6 h-6 ${c.fg}`} />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Second Row */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Students by Year */}
                    <div className="bg-white rounded-lg border border-gray-200 p-6" data-testid="students-by-year">
                        <h2 className="font-heading text-lg font-semibold text-ica-navy mb-4">Students by Year</h2>
                        <div className="space-y-4">
                            {['year1', 'year2', 'year3'].map((year, idx) => {
                                const count = stats?.students_by_year?.[year] || 0;
                                const total = stats?.total_students || 1;
                                const percentage = Math.round((count / total) * 100) || 0;
                                return (
                                    <div key={year}>
                                        <div className="flex justify-between text-sm mb-1">
                                            <span className="font-medium text-gray-700">Year {idx + 1}</span>
                                            <span className="text-gray-500">{count} students</span>
                                        </div>
                                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                                            <div 
                                                className="h-full rounded-full transition-all duration-500"
                                                style={{ 
                                                    width: `${percentage}%`,
                                                    backgroundColor: idx === 0 ? '#1e3a5f' : idx === 1 ? '#bc953f' : '#79a1b8'
                                                }}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Recent Lessons */}
                    <div className="lg:col-span-2 bg-white rounded-lg border border-gray-200 p-6" data-testid="recent-lessons">
                        <h2 className="font-heading text-lg font-semibold text-ica-navy mb-4">Recent Lessons</h2>
                        {stats?.recent_lessons?.length > 0 ? (
                            <div className="space-y-3">
                                {stats.recent_lessons.map((lesson) => (
                                    <div 
                                        key={lesson.id}
                                        className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                                    >
                                        <div className="w-10 h-10 bg-ica-gold/10 rounded-lg flex items-center justify-center flex-shrink-0">
                                            <ChefHat className="w-5 h-5 text-ica-gold" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-gray-900 truncate">{lesson.title}</p>
                                            <p className="text-sm text-gray-500">Year {lesson.year}</p>
                                        </div>
                                        <div className="flex items-center gap-2 text-sm text-gray-500">
                                            <Calendar className="w-4 h-4" />
                                            {lesson.date}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-8 text-gray-500">
                                <ChefHat className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                                <p>No lessons yet</p>
                                <p className="text-sm">Create your first lesson to get started</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default DashboardPage;
